import React, { useState, useEffect, useRef, FC, MouseEvent } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'

import { debounce, throttle } from '@utils'

import {
  LOCAL_STORAGE_KEY_NEWSLETTER_SUBSCRIBER,
  LOCAL_STORAGE_KEY_NEWSLETTER_NOTHANKS,
} from "@constants"

import { getImgixImageData, isClientSide, isLocalStorageKeySet, setLocalStorageKeyValue } from "@utils"

import useIsBeaconReady from '@hooks/useIsBeaconReady'

import NewsletterForm from '@components/HubspotForm/presets/NewsletterForm'
import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"

import "./NewsletterPopIn.scss"

export type NewsletterPopInProps = {
  distanceFromBottomToHide?: number,
  // The `forceShow` prop only exists so that we can show an example in Storybook!
  forceShow?: boolean,
}

const isSubscriber = (): boolean => isLocalStorageKeySet(LOCAL_STORAGE_KEY_NEWSLETTER_SUBSCRIBER)

const NewsletterPopIn: FC<NewsletterPopInProps> = ({
  distanceFromBottomToHide = 1200,
  forceShow,
}) => {
  const isBeaconReady = useIsBeaconReady()
  const [isHidden, setIsHidden] = useState<boolean>(!forceShow)
  const [isNeeded, setIsNeeded] = useState<boolean>(true)
  const divElRef = useRef<HTMLDivElement | null>(null)

  const hideAndRemove = (hiddenDelay = 0, isNeededDelay = 1) => {
    setTimeout(() => {
      setIsHidden(true)
    }, hiddenDelay)

    setTimeout(() => {
      setIsNeeded(false)
    }, isNeededDelay)
  }

  const handleScroll = debounce(() => {
    const scrollPosition = window.scrollY
    const windowHeight = window.innerHeight
    const bodyHeight = document.body.clientHeight

    const percentFromTop = Math.floor((scrollPosition + windowHeight) / bodyHeight * 100)
    const distanceFromBottom = bodyHeight - windowHeight - scrollPosition

    const _isHidden = !(percentFromTop >= 20 && distanceFromBottom >= distanceFromBottomToHide)

    setIsHidden(_isHidden)
  }, 150)

  // Show/hide scroll handling
  useEffect(() => {
    if (
      !isClientSide() ||
      isLocalStorageKeySet(LOCAL_STORAGE_KEY_NEWSLETTER_NOTHANKS) ||
      isSubscriber() ||
      forceShow
    )
      return

    const handleScrollThrottled = throttle(handleScroll, 150)
    document.addEventListener('scroll', handleScrollThrottled)

    return () => {
      document.removeEventListener('scroll', handleScrollThrottled)
    }
  }, [])

  // Close when Beacon is activated externally
  useEffect(() => {
    if (
      !isClientSide() ||
      isLocalStorageKeySet(LOCAL_STORAGE_KEY_NEWSLETTER_NOTHANKS) ||
      isSubscriber() ||
      !isBeaconReady ||
      !window.Beacon
    )
      return

    const onOpenBeacon = () => {
      hideAndRemove(0, 1000)
    }

    const onCloseBeacon = () => {
      setIsNeeded(true)
    }

    window.Beacon("on", "open", onOpenBeacon)
    window.Beacon("on", "close", onCloseBeacon)

    return () => {
      window.Beacon("off", "open", onOpenBeacon)
      window.Beacon("off", "close", onCloseBeacon)
    }
  }, [isBeaconReady])

  const handleSubmittedForm = () => {
    setLocalStorageKeyValue(LOCAL_STORAGE_KEY_NEWSLETTER_SUBSCRIBER, "true")
    document.removeEventListener('scroll', handleScroll)

    hideAndRemove(1500, 2500)
  }

  const handleClickClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setLocalStorageKeyValue(LOCAL_STORAGE_KEY_NEWSLETTER_NOTHANKS, "true")
    document.removeEventListener('scroll', handleScroll)
    hideAndRemove()
  }

  return isNeeded ? (
    <div
      className={`NewsletterPopIn${isHidden ? ' is-hidden' : ''}`}
      ref={divElRef}
    >
      <GatsbyImage
        alt=""
        className="texture-bg"
        image={getImgixImageData(
          "/images/components/NewsletterPopIn/NewsletterPopIn--texture.png",
          343,
          1.38
        )}
        role="presentation"
      />
      <h5>We've got more to share</h5>
      <NewsletterForm
        isCompact
        onSubmitted={handleSubmittedForm}
      />
      <button
        className="NewsletterPopIn--CloseButton"
        onClick={handleClickClose}
      >
        <Icon
          color=""
          size="32px"
          type={ICON_TYPES["close"]}
        />
      </button>
    </div>
  ) : null
}

export default NewsletterPopIn
